import React, { useState, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import '@ant-design/compatible/assets/index.css';
import { Layout, Button } from 'antd';
import { EmptyState } from '../empty_state/EmptyState';
import {
	compareStrings,
	getElementAt,
	getTimeZoneFieldName,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { FILTER_CONFIG_NAMES, FILTER_FIELD_TYPE, ROLE_TYPES } from '../../utils/DataConstants';
import OWDateRangePicker from '../ow_date_range_picker/OWDateRangePicker';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import {
	siteSurveysGroupedRestCrudThunksForBuyer,
	siteSurveysGroupedRestCrudThunksForSupplier,
	archiveGroupedSiteSurveys,
	unarchiveGroupedSiteSurveys,
} from '../../thunks/site_surveys_grouped_thunks';
import { updateFiltersEntities } from '../../thunks/thunks_wrapper';
import ReduxDataTable from '../redux_data_table/ReduxDataTable';
import { PAGE_SIZE_OPTIONS } from '../../constants/pagination';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import './index.less';
import { useCurrentUser } from '../../hooks/sessionHooks';
import DateTimeHover from '../date_time_component/DateTime';
import { useFilters } from '../../hooks/useFilters';
import { getSiteSurveyFilterConfig } from '../../utils/SiteSurveyFilterUtils';

const { Content } = Layout;

const SITE_SURVEYS_OVERVIEW_TARGET_COLLECTION = 'IndexPage';
const SITE_SURVEYS_ADVANCED_FILTERS = 'siteSurveysAdvancedFilters';

const SiteSurveysGrouped = (props) => {
	const {
		siteSurveysGrouped,
		archiveSiteSurvey,
		unarchiveSiteSurvey,
		updateFilters: updateFiltersDispatch,
	} = props;
	const { userType } = useCurrentUser();
	const history = useHistory();

	const [sortOrder, setSortOrder] = useState('');
	const [sortBy, setSortBy] = useState(null);

	const handleSortChange = (sorter) => {
		setSortBy(sorter.sort_by);
		setSortOrder(sorter.order);
	};

	const location = useLocation();

	const currentTab = useMemo(() => {
		const pathName = nullSafeGetOrElse('pathname', location, '');
		return getElementAt(pathName.split('/'), -1) || 'all';
	}, [location]);

	const isArchived = useMemo(() => compareStrings(currentTab, 'archived'), [currentTab]);
	const [archiveLoadingKey, setArchiveLoadingKey] = useState(null);

	const getRecordKey = (record) =>
		`${nullSafeGet('title', record)}-${nullSafeGet('templateId', record)}`;

	const handleArchive = useCallback(
		(record, e) => {
			e.stopPropagation();
			const title = nullSafeGet('title', record);
			const templateId = nullSafeGet('templateId', record);
			if (!title || !templateId) return;
			setArchiveLoadingKey(getRecordKey(record));
			archiveSiteSurvey(userType, title, templateId)
				.then(() => {
					updateFiltersDispatch(userType, history, { isArchived });
				})
				.catch(() => null)
				.finally(() => setArchiveLoadingKey(null));
		},
		[archiveSiteSurvey, updateFiltersDispatch, userType, history, isArchived]
	);

	const handleUnarchive = useCallback(
		(record, e) => {
			e.stopPropagation();
			const title = nullSafeGet('title', record);
			const templateId = nullSafeGet('templateId', record);
			if (!title || !templateId) return;
			setArchiveLoadingKey(getRecordKey(record));
			unarchiveSiteSurvey(userType, title, templateId)
				.then(() => {
					updateFiltersDispatch(userType, history, { isArchived });
				})
				.catch(() => null)
				.finally(() => setArchiveLoadingKey(null));
		},
		[unarchiveSiteSurvey, updateFiltersDispatch, userType, history, isArchived]
	);

	const getSiteSurveyTemplateLink = useCallback(
		(sst) => `/${userType}/siteSurveys/templates/detail/${sst.id}/details`,
		[userType]
	);

	const columns = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: 'title',
				key: 'title',
				render: (text, record) => (
					<div className="SiteSurveysGroupedRow">
						<div>
							<div className="SiteSurveysGroupedRow__rowTitle">
								<Ellipsis tooltip={true} length={64}>
									{nullSafeGet('title', record)}
								</Ellipsis>
							</div>
							<div className="SiteSurveysGroupedRow__rowSubtitle">
								<div className="SiteSurveysGroupedRow__rowInlineGroup">
									<i className="icons8-font icons8-location-marker"></i> {record.numberOfLocations}{' '}
									Locations
								</div>
							</div>
							{nullSafeGet('siteSurveyTemplate.title', record) ? (
								<div className="SiteSurveysGroupedRow__rowSubtitle">
									<div className="SiteSurveysGroupedRow__rowInlineGroup">
										<i className="icons8-font icons8-clipboard-checklist"></i>
										{nullSafeGet('siteSurveyTemplate.title', record)}
									</div>
								</div>
							) : null}
						</div>
						<div className="SiteSurveysGroupedRow__rightSide">
							{record.lastWalkthroughCompleted ? (
								<div className="SiteSurveysGroupedRow__rowSubtitle">
									Last walkthrough{' '}
									<DateTimeHover
										timestamp={record.lastWalkthroughCompleted}
										showDate
										showTime
										timeZone={nullSafeGet('lastWalkthroughCompletedLocationTimeZoneId', record)}
									/>
								</div>
							) : null}
							<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_SITE_SURVEYS}>
								<div className="ActionWrapper mt-[5px]">
									{isArchived ? (
										<Button
											className="ActionButton delete"
											loading={archiveLoadingKey === getRecordKey(record)}
											disabled={
												archiveLoadingKey !== null && archiveLoadingKey !== getRecordKey(record)
											}
											onClick={(e) => handleUnarchive(record, e)}
										>
											<i className="icons8-font icons8-font-margin-right icons8-trash-can"></i>{' '}
											Unarchive
										</Button>
									) : (
										<Button
											className="ActionButton delete"
											loading={archiveLoadingKey === getRecordKey(record)}
											disabled={
												archiveLoadingKey !== null && archiveLoadingKey !== getRecordKey(record)
											}
											onClick={(e) => handleArchive(record, e)}
										>
											<i className="icons8-font icons8-font-margin-right icons8-trash-can"></i>{' '}
											Archive
										</Button>
									)}
								</div>
							</AccessPermissionChecker>
						</div>
					</div>
				),
				sorter: (a, b) => a[sortBy] - b[sortBy],
				sortDirections: 'descend',
				sortOrder: sortOrder,
			},
		],
		[sortBy, sortOrder, isArchived, archiveLoadingKey, handleArchive, handleUnarchive]
	);

	const updateFilters = useCallback(
		(filters) => updateFiltersDispatch(userType, history, filters),
		[updateFiltersDispatch, userType, history]
	);

	const siteSurveyFilters = useFilters({
		filterTargetCollectionName: SITE_SURVEYS_ADVANCED_FILTERS,
		overviewCollectionName: SITE_SURVEYS_OVERVIEW_TARGET_COLLECTION,
		updateFilters,
	});

	const filterConfig = useMemo(
		() => getSiteSurveyFilterConfig({ filters: siteSurveyFilters }),
		[siteSurveyFilters]
	);

	const onRow = useCallback(
		(record) => ({
			onClick: () => {
				let searchString = location.search;
				if (searchString[0] === '?') {
					searchString = searchString.slice(1);
				}
				let backUrl = `/${userType}/siteSurveys/byTitle/${encodeURIComponent(
					record.title
				)}/template/${nullSafeGet('templateId', record)}/locations`;
				const extraParams = [];
				if (searchString && searchString.length > 0) {
					extraParams.push(`backlinkParams=${encodeURIComponent(searchString)}`);
				}
				if (isArchived) {
					extraParams.push('isArchived=true');
				}
				if (extraParams.length > 0) {
					backUrl = backUrl.concat(`?${extraParams.join('&')}`);
				}
				history.push(backUrl);
			},
		}),
		[location.search, userType, history, isArchived]
	);

	return (
		<Content className="siteSurveysPage" style={{ padding: '0 0.5em' }}>
			<ReduxDataTable
				preAppliedFilters={{ isArchived }}
				rightActions={
					!isArchived ? (
						<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_SITE_SURVEYS}>
							<Button
								type="primary"
								size="large"
								onClick={() => history.push(`/${userType}/siteSurveys/overview/new`)}
							>
								Add Site Surveys
							</Button>
						</AccessPermissionChecker>
					) : null
				}
				handleQueryParams
				filterConfig={filterConfig}
				emptyState={
					<EmptyState
						graphic={
							<img
								alt="Currently there are no site surveys!"
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
							/>
						}
						headline={"It's a little lonely in here."}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 440, marginBottom: 16 }}>
									Looks like your team has not added any site surveys yet.
								</div>
							</div>
						}
					/>
				}
				onTableRow={onRow}
				showHeader={false}
				targetCollectionName={SITE_SURVEYS_OVERVIEW_TARGET_COLLECTION}
				tableColumns={columns}
				keyAccessor={(el) => el.id}
				initialPagination={{ current: 1, pageSize: 10 }}
				hiddenPagination={{ showSizeChanger: true, pageSizeOptions: PAGE_SIZE_OPTIONS }}
				fetchData={
					userType === ROLE_TYPES.SUPPLIER
						? siteSurveysGroupedRestCrudThunksForSupplier
						: siteSurveysGroupedRestCrudThunksForBuyer
				}
				stateSlice={siteSurveysGrouped}
				entityCollectionName="site_surveys/grouped"
				initialSorting={{ sort_by: 'name', order: 'ascend' }}
				sortDirections={'descend'}
				sortOrder={sortOrder}
				sortField={sortBy}
				sorters={[
					{
						label: 'Total Locations',
						selectVal: 'numberOfLocations',
						value: {
							sort_by: 'numberOfLocations',
							order: 'desc',
						},
					},
					{
						label: 'Total Walkthroughs',
						selectVal: 'totalWalkThroughs',
						value: {
							sort_by: 'totalWalkThroughs',
							order: 'desc',
						},
					},
				]}
				localSorting={handleSortChange}
			/>
		</Content>
	);
};

const mapStateToProps = (state, ownProps) => ({
	currentUser: state.session.currentUser,
	siteSurveys: state.site_surveys,
	siteSurveysGrouped: state['site_surveys/grouped'],
});

const getThunksForRole = (userType) =>
	userType === ROLE_TYPES.SUPPLIER
		? siteSurveysGroupedRestCrudThunksForSupplier
		: siteSurveysGroupedRestCrudThunksForBuyer;

const mapDispatchToProps = (dispatch) => ({
	archiveSiteSurvey: (userType, title, templateId) =>
		dispatch(archiveGroupedSiteSurveys(userType)(title, templateId, false)),
	unarchiveSiteSurvey: (userType, title, templateId) =>
		dispatch(unarchiveGroupedSiteSurveys(userType)(title, templateId, false)),
	updateFilters: (userType, history, filters) =>
		dispatch(
			updateFiltersEntities(userType, 'site_surveys/grouped', getThunksForRole(userType), null)(
				true,
				history,
				SITE_SURVEYS_OVERVIEW_TARGET_COLLECTION,
				filters,
				false
			)
		),
});

export default connect(mapStateToProps, mapDispatchToProps)(SiteSurveysGrouped);
